import React, { useEffect, useState } from "react";
import {
  clearUpdateUserSuccess,
  getLogUserRequest,
  getReferralRequest,
  updateUserRequest,
} from "../../redux/action";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { useFormik } from "formik";
import * as yup from "yup";
import { useHistory, Link } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import UserCurrencyDisplay from "../Currency/UserCurrencyDisplay";
import CurrencyStatusIndicator from "../Currency/CurrencyStatusIndicator";
import { currencySymbols } from "../../utils/currency";
import axios from "../../redux/sagas/axios";

// Icons
import {
  UserIcon,
  PhoneIcon,
  CreditCardIcon,
  LibraryIcon, // For Bank
  CurrencyDollarIcon,
  IdentificationIcon,
  BriefcaseIcon,
  GlobeAltIcon,
  CashIcon,
} from "@heroicons/react/outline";

// --- Reusable Input Component ---
const InputField = ({
  label,
  name,
  type = "text",
  icon: Icon,
  formik,
  placeholder,
  prefix,
}) => {
  const hasError = formik.touched[name] && formik.errors[name];

  return (
    <div className="w-full">
      <label className="block text-xs font-heading font-bold text-gray-700 uppercase tracking-wide mb-2">
        {label}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {prefix ? (
            <span className="text-gray-500 font-bold">{prefix}</span>
          ) : (
            <Icon
              className={`h-5 w-5 ${
                hasError ? "text-red-400" : "text-gray-400"
              }`}
            />
          )}
        </div>
        <input
          type={type}
          name={name}
          {...formik.getFieldProps(name)}
          placeholder={placeholder}
          className={`
            w-full pl-10 pr-4 py-3 rounded-xl border font-body text-sm transition-all outline-none
            ${
              hasError
                ? "border-red-300 bg-red-50 text-red-900 focus:border-red-500 placeholder-red-300"
                : "border-gray-200 bg-gray-50 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 text-gray-900"
            }
          `}
        />
      </div>
      {hasError && (
        <p className="mt-1 text-xs text-red-500 font-medium flex items-center gap-1">
          {formik.errors[name]}
        </p>
      )}
    </div>
  );
};

// --- Currency Dropdown Component ---
const CurrencyDropdown = ({
  label,
  name,
  formik,
  validCurrencies,
  loading,
}) => {
  const hasError = formik.touched[name] && formik.errors[name];

  return (
    <div className="w-full">
      <label className="block text-xs font-heading font-bold text-gray-700 uppercase tracking-wide mb-2">
        {label}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <GlobeAltIcon
            className={`h-5 w-5 ${hasError ? "text-red-400" : "text-gray-400"}`}
          />
        </div>
        <select
          name={name}
          {...formik.getFieldProps(name)}
          disabled={loading}
          className={`
            w-full pl-10 pr-4 py-3 rounded-xl border font-body text-sm transition-all outline-none appearance-none
            ${
              hasError
                ? "border-red-300 bg-red-50 text-red-900 focus:border-red-500"
                : "border-gray-200 bg-gray-50 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 text-gray-900"
            }
            ${loading ? "opacity-50 cursor-not-allowed" : ""}
          `}
        >
          <option value="">Use System Default</option>
          {validCurrencies.map((currency) => (
            <option key={currency} value={currency}>
              {currencySymbols[currency] || ""} {currency}
            </option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <svg
            className="h-4 w-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
      {hasError && (
        <p className="mt-1 text-xs text-red-500 font-medium flex items-center gap-1">
          {formik.errors[name]}
        </p>
      )}
    </div>
  );
};

export const ProfileForm = ({ user, loading }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [validCurrencies, setValidCurrencies] = useState([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);

  const {
    updateUserError,
    updateUserSuccess,
    updateUserLoading,
    token,
    singleUser,
    referralUser,
  } = useSelector((state) => {
    const {
      success: { updateUser: updateUserSuccess },
      errors: { updateUser: updateUserError },
    } = state.ajaxStatuses;

    const { updateUserLoading } = state.loadingIndicator;
    const { user, token, singleUser, referralUser } = state.userData;

    return {
      singleUser,
      updateUserError,
      updateUserSuccess,
      updateUserLoading,
      token,
      referralUser,
    };
  });

  const formik = useFormik({
    initialValues: {
      userName: "",
      phone: "",
      walletAddress: "",
      bankName: "",
      accountNumber: "",
      accountName: "",
      referralBonus: "",
      balance: "",
      preferredCurrency: "",
      receiveAmount: "", // Add this
      receiveDurationDays: "", // Add this
    },
    validationSchema: yup.object({
      userName: yup
        .string()
        .required("User name is required")
        .max(32, "User name is too long")
        .trim(),
      bankName: yup.string().trim(),
      accountNumber: yup.string().trim(),
      accountName: yup.string().trim(),
      preferredCurrency: yup.string().trim(),
      receiveAmount: yup.number().min(0, "Amount must be positive").nullable(),
      receiveDurationDays: yup
        .number()
        .min(1, "Duration must be at least 1 day")
        .nullable(),
    }),
    onSubmit: (prop) => {
      let formData = prop;
      const id = user._id;
      dispatch(updateUserRequest({ formData, id }));
    },
  });

  // Fetch valid currencies on component mount
  useEffect(() => {
    const fetchValidCurrencies = async () => {
      try {
        setCurrenciesLoading(true);
        const response = await axios.get("/currency/valid-currencies");
        setValidCurrencies(response.data.currencies || []);
      } catch (error) {
        console.error("Error fetching valid currencies:", error);
        // Fallback to common currencies if API fails
        setValidCurrencies(["USD", "ZMW", "EUR", "GBP", "NGN", "GHS", "KES"]);
      } finally {
        setCurrenciesLoading(false);
      }
    };

    fetchValidCurrencies();
  }, []);

  useEffect(() => {
    if (updateUserError) toast.error(updateUserError, { duration: 3000 });
  }, [updateUserError]);

  useEffect(() => {
    if (updateUserSuccess) {
      toast.success(updateUserSuccess, { duration: 3000 });
      dispatch(clearUpdateUserSuccess());
    }
  }, [updateUserSuccess]);

  useEffect(() => {
    dispatch(getLogUserRequest(token));
  }, [dispatch, token]);

  useEffect(() => {
    if (user?.referralUser) {
      dispatch(getReferralRequest(user.referralUser));
    }
  }, [user, dispatch]);

  useEffect(() => {
    if (user) {
      formik.setValues((values) => ({
        ...values,
        userName: user.userName || "",
        phone: user.phone || "",
        walletAddress: user.walletAddress || "",
        bankName: user.bankName || "",
        accountNumber: user.accountNumber || "",
        accountName: user.accountName || "",
        referralBonus: user.referralBonus || "",
        balance: user.balance || "",
        preferredCurrency: user.preferredCurrency || "",
        receiveAmount: user.receiveAmount || "",
        receiveDurationDays: user.receiveDurationDays || "",
      }));
    }
  }, [user]);

  return (
    <div className="min-h-screen bg-gray-50 py-10 font-body text-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* --- HEADER --- */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-gray-900">
              Edit Profile
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage user details, banking information, and account balance.
            </p>
          </div>
          <Link
            to={`/admin/investments/${user._id}`}
            className="inline-flex items-center justify-center px-6 py-2.5 bg-white border border-gray-200 rounded-full text-sm font-bold text-primary hover:bg-gray-50 hover:text-primaryDark shadow-sm transition-all"
          >
            <BriefcaseIcon className="w-4 h-4 mr-2" />
            View Investments
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* --- LEFT COLUMN: USER SUMMARY CARD --- */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 p-6 sticky top-8">
              <h2 className="text-sm font-heading font-bold text-gray-400 uppercase tracking-wider mb-6">
                User Overview
              </h2>

              {loading ? (
                <div className="flex items-center justify-center py-10">
                  <CircularProgress size={30} style={{ color: "#4F46E5" }} />
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  {/* Avatar */}
                  <div className="relative mb-4">
                    <div className="w-24 h-24 bg-gradient-to-br from-primary to-blue-400 rounded-full flex items-center justify-center shadow-lg shadow-primary/30">
                      <span className="text-white font-heading font-bold text-4xl">
                        {user.userName
                          ? user.userName.charAt(0).toUpperCase()
                          : "U"}
                      </span>
                    </div>
                    <div className="absolute bottom-0 right-0 w-6 h-6 bg-green-400 border-4 border-white rounded-full"></div>
                  </div>

                  <h3 className="text-xl font-heading font-bold text-gray-900">
                    {user.userName || "Unknown User"}
                  </h3>
                  <p className="text-sm text-gray-500 mb-6">{user.email}</p>

                  {/* Stats List */}
                  <div className="w-full space-y-4 border-t border-gray-100 pt-6">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Account ID</span>
                      <span className="text-sm font-mono font-medium bg-gray-100 px-2 py-1 rounded text-gray-700">
                        {user._id?.slice(-6).toUpperCase()}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Balance</span>
                      <span className="text-base font-bold text-green-600">
                        <UserCurrencyDisplay
                          usdAmount={user.balance || 0}
                          userId={user._id}
                          className="inline"
                        />
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Ref Bonus</span>
                      <span className="text-base font-bold text-primary">
                        <UserCurrencyDisplay
                          usdAmount={user.referralBonus || 0}
                          userId={user._id}
                          className="inline"
                        />
                      </span>
                    </div>

                    {user.preferredCurrency && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Currency</span>
                        <span className="text-sm font-medium bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          {currencySymbols[user.preferredCurrency] || ""}{" "}
                          {user.preferredCurrency}
                        </span>
                      </div>
                    )}

                    {user.referralUser && referralUser && (
                      <div className="bg-primary/5 rounded-xl p-4 mt-4">
                        <p className="text-xs font-bold text-primary uppercase mb-1">
                          Referred By
                        </p>
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {referralUser.email}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* --- RIGHT COLUMN: EDIT FORM --- */}
          <div className="lg:col-span-2">
            <form
              onSubmit={formik.handleSubmit}
              className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 p-6 md:p-8"
            >
              {/* Section 1: Personal Info */}
              <div className="mb-8">
                <h3 className="text-lg font-heading font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <UserIcon className="w-5 h-5 text-primary" />
                  Personal Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputField
                    label="Username"
                    name="userName"
                    icon={UserIcon}
                    formik={formik}
                    placeholder="JohnDoe"
                  />
                  <InputField
                    label="Phone Number"
                    name="phone"
                    icon={PhoneIcon}
                    formik={formik}
                    placeholder="+1 (555) 000-0000"
                  />
                  <div className="md:col-span-2">
                    <InputField
                      label="Wallet Address"
                      name="walletAddress"
                      icon={IdentificationIcon}
                      formik={formik}
                      placeholder="0x..."
                    />
                  </div>
                </div>
              </div>

              <div className="w-full h-px bg-gray-100 my-8"></div>

              {/* Section 2: Currency Settings */}
              <div className="mb-8">
                <h3 className="text-lg font-heading font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <GlobeAltIcon className="w-5 h-5 text-primary" />
                  Currency Preferences
                </h3>
                <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100">
                  <div className="mb-4">
                    <p className="text-sm text-blue-700 mb-2">
                      Set a preferred currency for this user. If no currency is
                      selected, the system default currency will be used for
                      displaying balances.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <CurrencyDropdown
                      label="Preferred Currency"
                      name="preferredCurrency"
                      formik={formik}
                      validCurrencies={validCurrencies}
                      loading={currenciesLoading}
                    />
                    <div className="flex flex-col justify-center">
                      <label className="block text-xs font-heading font-bold text-gray-700 uppercase tracking-wide mb-2">
                        Current Status
                      </label>
                      <CurrencyStatusIndicator userId={user._id} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full h-px bg-gray-100 my-8"></div>

              {/* Section 3: Banking Info */}
              <div className="mb-8">
                <h3 className="text-lg font-heading font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <LibraryIcon className="w-5 h-5 text-primary" />
                  Bank Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputField
                    label="Bank Name"
                    name="bankName"
                    icon={LibraryIcon}
                    formik={formik}
                    placeholder="Chase Bank"
                  />
                  <InputField
                    label="Account Name"
                    name="accountName"
                    icon={UserIcon}
                    formik={formik}
                    placeholder="John Doe"
                  />
                  <div className="md:col-span-2">
                    <InputField
                      label="Account Number"
                      name="accountNumber"
                      icon={CreditCardIcon}
                      formik={formik}
                      placeholder="0000 0000 0000"
                    />
                  </div>
                </div>
              </div>

              <div className="w-full h-px bg-gray-100 my-8"></div>

              {/* Section 4: Financials (Admin Only) */}
              <div className="mb-8">
                <h3 className="text-lg font-heading font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <CurrencyDollarIcon className="w-5 h-5 text-primary" />
                  Financial Adjustments
                </h3>
                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InputField
                      label="Account Balance"
                      name="balance"
                      prefix="K"
                      formik={formik}
                      placeholder="0.00"
                      type="number"
                    />
                    <InputField
                      label="Referral Bonus"
                      name="referralBonus"
                      prefix="K"
                      formik={formik}
                      placeholder="0.00"
                      type="number"
                    />
                  </div>
                </div>
              </div>

              <div className="w-full h-px bg-gray-100 my-8"></div>

              {/* Section 5: Reward Settings (Admin Only) */}
              <div className="mb-8">
                <h3 className="text-lg font-heading font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <CashIcon className="w-5 h-5 text-primary" />
                  Reward Settings
                </h3>
                <div className="bg-purple-50 rounded-2xl p-6 border border-purple-100">
                  <div className="mb-4">
                    <p className="text-sm text-purple-700 mb-2">
                      Configure automatic reward distribution for this user. The
                      user will receive the specified amount at regular
                      intervals.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InputField
                      label="Receive Amount"
                      name="receiveAmount"
                      prefix="$"
                      formik={formik}
                      placeholder="100.00"
                      type="number"
                    />
                    <InputField
                      label="Duration (Days)"
                      name="receiveDurationDays"
                      icon={CashIcon}
                      formik={formik}
                      placeholder="7"
                      type="number"
                    />
                  </div>
                  <div className="mt-4 bg-white rounded-xl p-4 border border-purple-200">
                    <p className="text-xs text-gray-600">
                      <span className="font-bold">Example:</span> If you set
                      Receive Amount to $100 and Duration to 7 days, the user
                      will automatically receive $100 every 7 days.
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex items-center justify-end pt-4">
                <button
                  type="submit"
                  disabled={updateUserLoading}
                  className="inline-flex items-center px-8 py-3.5 bg-primary hover:bg-primaryDark text-white font-heading font-bold rounded-xl shadow-lg shadow-primary/30 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed hover:-translate-y-1"
                >
                  {updateUserLoading ? (
                    <>
                      <CircularProgress
                        size={20}
                        color="inherit"
                        className="mr-2"
                      />
                      Saving Changes...
                    </>
                  ) : (
                    "Update Profile"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
